import AuthorBlock from '@atoms/AuthorBlock/AuthorBlock';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import ThemeEnum from '@models/enums/ThemeEnum';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useRef } from 'react';
import SplitType from 'split-type';

gsap.registerPlugin(ScrollTrigger);

interface Props {
  readonly backgroundColour: string;
  readonly bodyText: string;
  readonly downloadCta?: ILink;
  readonly name?: string;
  readonly orderCta?: ILink;
  readonly role?: string;
  readonly signatureImage?: IImage;
  readonly theme: ThemeEnum;
}

const IntroductionModule = ({
  backgroundColour = '#fff',
  bodyText,
  downloadCta,
  orderCta,
  name,
  role,
  signatureImage,
  theme = ThemeEnum.Light,
}: Props) => {
  const animatedTextRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const authorBlockRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(752);

  useEffect(() => {
    const animatedText = animatedTextRef.current;
    const image = imageRef.current;
    const authorBlock = authorBlockRef.current;

    if (!animatedText || isMobile) return;

    const text = new SplitType(animatedText, { types: 'words' });
    const words = text.words;

    const baseAnimation = {
      from: { opacity: 0.2, y: 8 },
      to: { opacity: 1, y: 0, duration: 2, ease: 'power4.out' },
    };

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: animatedText,
        start: 'top bottom-=10%',
        end: 'bottom top+=60%',
        scrub: true,
        once: true,
      },
    });

    tl.fromTo(words, baseAnimation.from, {
      ...baseAnimation.to,
      stagger: 0.1,
    });

    if (image) {
      tl.fromTo(image, baseAnimation.from, baseAnimation.to, '-=1.5');
    }

    if (authorBlock) {
      tl.fromTo(authorBlock, baseAnimation.from, baseAnimation.to, '-=1');
    }

    return () => {
      tl.kill();
      text.revert();
    };
  }, [isMobile]);

  return (
    <section
      className={classNames('tw-component-padding', {
        'tw-dark': theme === ThemeEnum.Dark,
      })}
      data-background-color={backgroundColour}
    >
      <div className="tw-container tw-flex tw-flex-col tw-gap-sm tw-py-5xl tw-text-grey-80 md:tw-flex-row md:tw-gap-0 dark:tw-text-white">
        <div className="tw-flex tw-flex-col tw-gap-sm md:tw-order-1 md:tw-w-1/2">
          <div
            ref={animatedTextRef}
            className="tw-rich-text tw-font-sans tw-text-xl tw-font-light tw-antialiased tw-will-change-auto md:tw-text-2xl md:tw-leading-[34px]"
            dangerouslySetInnerHTML={{ __html: bodyText }}
          />

          <div className="tw-flex tw-flex-col tw-gap-sm">
            {signatureImage && (
              <div ref={imageRef}>
                <Image image={signatureImage} imageClasses="tw-max-h-[76px]" />
              </div>
            )}

            {name && role && (
              <div ref={authorBlockRef}>
                <AuthorBlock
                  author={name}
                  authorPosition={role}
                  theme={theme}
                />
              </div>
            )}
          </div>
        </div>
        <div className="tw-flex md:tw-w-1/2">
          <div className="tw-flex tw-w-full tw-flex-col tw-gap-xs md:tw-w-auto md:tw-min-w-[206px]">
            {downloadCta?.text && downloadCta?.url && (
              <Button
                modifier="fill"
                variant={theme === ThemeEnum.Dark ? 'white' : 'grey80'}
                label={downloadCta.text}
                as="link"
                href={downloadCta.url}
                iconName="download"
                iconClasses={
                  theme === ThemeEnum.Dark ? 'tw-text-grey-80' : 'tw-text-white'
                }
                target={downloadCta.target}
                onClick={() => {
                  window.dataLayer.push({
                    event: 'trackingEvent_file_download',
                    event_name_variable: 'file_download',
                    trackingEvent_file_download: {
                      document_name: 'colour chart 2024',
                      document_language: null,
                      document_type: null,
                    },
                  });
                }}
              />
            )}
            {orderCta?.text && orderCta?.url && (
              <Button
                modifier="outline"
                variant={theme === ThemeEnum.Dark ? 'white' : 'grey80'}
                label={orderCta.text}
                as="link"
                href={orderCta.url}
                target={orderCta.target}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntroductionModule;
